import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable, map,
  of, tap, concatMap, from, toArray, switchMap, take, BehaviorSubject, skip,
  throwError,
  catchError
} from 'rxjs';
import { Lesson, LessonIndex, LessonTab } from '../../core/interfaces/lesson';
import { RouterService } from 'src/app/shared/services/router.service';
import { TabsService } from './tabs.service';
import { ElementsService } from './elements.service';
import { TimeService } from 'src/app/shared/services/time.service';
import { Element } from 'src/app/core/interfaces/element';
import { environment } from 'src/environments/environments';
import isEqual from 'lodash.isequal';
import { UsersService } from './users.service';
import { CourseLesson } from 'src/app/core/interfaces/course-lesson';

@Injectable({
  providedIn: 'root'
})
export class LessonsService {

  public currentLesson: Lesson = {};
  public lastSavedLesson: Lesson = {};
  public lessonIndex: LessonIndex = {
    title: '',
    tabs: []
  };
  public elementsDeleted: boolean = false;

  public creating: boolean = false;
  public loading: boolean = false;

  private lessonsBaseUrl = environment.lessonsBaseUrl;
  private imagesBaseUrl = environment.imagesBaseUrl;
  private attachmentsBaseUrl = environment.attachmentsBaseUrl;

  private lessonLoadedEvent = new BehaviorSubject<string>('');
  public logoutWithoutSaving: boolean = false;

  constructor(
    private http: HttpClient,
    private routerService: RouterService,
    private tabsService: TabsService,
    private elementsService: ElementsService,
    private timeService: TimeService,
    private userService: UsersService
  ) {
  }

  toggleCreateLesson() {
    this.creating = !this.creating;
  }

  getAll(): Observable<any[]> {
    return this.http
    .get<any>(this.lessonsBaseUrl).pipe(
        map(lessons => lessons.map(lesson => { return {
          ...lesson,
          image: environment.s3BaseUrl + '/images/' + lesson.id + '_cover.jpeg',
          type: 'lesson' } })),
      );
  }

  getAllByTeacher(organizationId?: string): Observable<any[]> {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    
    return this.http
    .get<any>(`${this.lessonsBaseUrl}ByTeacher${params}`).pipe(
        map(lessons => lessons.map(lesson => {
          return {
          ...lesson,
          type: 'lesson' } })),
      );
  }

  getAllByOrganization(organizationId: string): Observable<any[]> {    
    return this.http
    .post<any>(`${this.lessonsBaseUrl}/getLessonsByOrganization`, {organizationId}).pipe(          
        map(resp => {
            if (!resp) return [];

            return Object.keys(resp).map((key) => {
              const lesson = resp[key];

              return {
                id: key,
                ...lesson,
                type: 'lesson'
              };
            });
        })
      );
  }

  getAllFromStudent(organizationId?: string): Observable<any[]> {
    const params = organizationId ? `?organizationId=${organizationId}` : '';

    return this.http
    .get<any>(`${this.lessonsBaseUrl}FromStudent${params}`).pipe(
        map(lessons => lessons.map(lesson => { return {
          ...lesson,
          type: 'lesson' } })),
      );
  }

  getLessonsByOrganization(organizationId: string): Observable<any[]> {
    return this.http.post<any>(`${this.lessonsBaseUrl}/getLessonsByOrganization`, {organizationId}).pipe(
      map(resp => {
          if (!resp) return [];

          return Object.keys(resp).map((key, index) => {
              const lesson = resp[key];
              // console.log(lesson)

              return {
                  id: key,
                  title: lesson.title,
                  teachers: Object.entries(lesson.teachers || {}).map(([teacherId, t]: any) => ({
                      id: teacherId,
                      email: t.email?.S || '',
                      firstname: t.firstname?.S || '',
                      lastname: t.lastname?.S || ''
                  })),
                  evaluators: Object.entries(lesson.evaluators || {}).map(([evaluatorId, e]: any) => ({
                      id: evaluatorId,
                      email: e.email?.S || '',
                      firstname: e.firstname?.S || '',
                      lastname: e.lastname?.S || ''
                  })),
                  students: lesson.students || 0,
                  rating: lesson.rating || null,
                  totalRating: lesson.totalRating || null,
                  published: lesson.published || false,
                  coverImage: lesson.coverImage || '',
                  programs: lesson.programs || [],
              };
          });
      })
    );
  }

  getOne(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.lessonsBaseUrl}/${id}`).pipe(
        map(lesson => {
          return {
          ...lesson,
          files: lesson.files ?? [],
          type: 'lesson' } }),
      );
  }

  getLesson(id: string): Observable<any> {
    return this.http.get(`${this.lessonsBaseUrl}/${id}`, { observe: 'response'});
  }

  // para el rol de TEACHER
  editOne(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.lessonsBaseUrl}/edit/${id}`).pipe(
        map(lesson => {
          return {
          ...lesson,
          files: lesson.files ?? [],
          type: 'lesson' } }),
      );
  }


  // para el rol de STUDENT
  getOneDetail(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.lessonsBaseUrl}/detail/${id}`).pipe(
        map(lesson => {
          return {
          ...lesson,
          files: lesson.files ?? [],
          type: 'lesson' } }),
      );
  }

  saveLessonSession(lesson:Lesson, sessionId?: string): Observable<any> {
    //console.log("saveLessonSession");
    //console.log(lesson);
    return this.http.post(`https://${environment.baseUrl}/php/saveLesson.php`, {lesson, sessionId})
  }

  getLessonSession(lessonId: string): Observable<any> {
  // console.log("getLessonSession", lessonId);
  this.loading = true;

  return this.http
    .get<any>(`https://${environment.baseUrl}/php/getLesson.php?lessonId=${lessonId}`).pipe(
      map(lesson => {
        const resultLesson = {
          ...lesson,
          image: environment.s3BaseUrl + '/images/' + lesson.id + '_cover.jpeg',
          files: lesson.files ?? [],
          type: 'lesson'
        };

        this.currentLesson = {
          ...resultLesson,
          initDate: resultLesson.published
            ? resultLesson.initDate
            : this.timeService.getMaxDate(resultLesson.initDate)
        };

        // console.log("Lesson loaded in getLessonSession:", this.currentLesson);

        this.lastSavedLesson = { ...this.currentLesson };
        this.lastSavedLesson.programs = [...(this.currentLesson.programs ?? [])];

        this.loading = false;
        return resultLesson;
      }),
      catchError(err => {
        this.loading = false;
        return throwError(() => err);
      })
    );
}

  getByCourseId(courseId: string): Observable<any[]> {
    return this.http
      .get<any>(`${this.lessonsBaseUrl}ByCourse/${courseId}`).pipe(
        map(lessons => lessons.map(lesson => { return {
          ...lesson,
          initDate: lesson.published ? lesson.initDate : this.timeService.getMaxDate(lesson.initDate),
          type: 'lesson' } })),
      );
  }

  getCourseLessonsByLesson(lessonId: string): Observable<CourseLesson[]> {
    return this.http.get<any>(`${this.lessonsBaseUrl}/getCourseLessonsByLesson/${lessonId}`).pipe(
      map(response => {
        console.log("getCourseLessonsByLesson response:", response);
        const lessons = response?.message ?? [];
        return lessons.map((lesson: any) => ({
          id: lesson.id,
          courseTitle: lesson.courseTitle,
          initDate: lesson.initDate,
          endDate: lesson.endDate,
          toqualify: lesson.toqualify ? parseFloat(lesson.toqualify) : undefined,
          tries: lesson.tries ? parseInt(lesson.tries, 10) : undefined,
          uniqueAnswers: lesson.uniqueAnswers,
          lessonDuration: lesson.lessonDuration,
          grademethod: lesson.grademethod,
          TEACHER: lesson.TEACHER,
          idLMS: lesson.idLMS ?? null,
          lessonId: lesson.lessonId,
          lessonTitle: lesson.lessonTitle,
        }));
      })
    );
  }

  loadLesson(edit:boolean = false, student: boolean = false) {
    return this.loadLessonInfo(edit, student).pipe(
      tap(() => { this.loading = false }),
    )
  }

  loadCompleteLesson(edit:boolean = false) {
    return this.loadLessonInfo(edit).pipe(
      switchMap(() => this.loadLessonTabs().pipe(take(1)))
    )

  }

  loadCompleteLessonEventListener() {
    return this.lessonLoadedEvent.asObservable().pipe(
      skip(1) // when we subscribe to BehaviorSubject, a first empty value is emmitted
    );
  }

  private loadLessonInfo(edit:boolean = false, student: boolean = false) {
    let lessonId = this.routerService.getCurrentRouteParams()['lessonId'];
    this.loading = true;

    let lesson$ = new Observable<any>();
    if (edit) {
      lesson$ = this.editOne(lessonId);
    } else if (student) {
      lesson$ = this.getOneDetail(lessonId);
    } else {
      lesson$ = this.getOne(lessonId);
    }
    return lesson$.pipe(
      tap({
        error: e => {
          this.loading = false;
        },
        next: lesson => {
          const timestamp = new Date().getTime();
          this.currentLesson = {
            ...lesson,
            initDate: lesson.published ? lesson.initDate : this.timeService.getMaxDate(lesson.initDate)
          };

          if (this.currentLesson.grademethod == null) {
            this.currentLesson.grademethod = 1;
          }
          this.lastSavedLesson = {...this.currentLesson};
          this.lastSavedLesson.programs = [...this.currentLesson.programs ?? []]
        }
      }),
    )
  }

  loadLessonTabs() {
    return this.tabsService.getByLessonId(this.currentLesson.id!).pipe(
      take(1),
      tap(tabs => {
        this.loading = true;
        this.currentLesson.tabs = tabs.map((tab) => {
          tab.pages = [];
          for (let j = 0; j < tab.npages!; j++) {
            tab.pages?.push({
              n: j + 1,
              elements: []
            })
          }
          return tab;
        })
      }),
      concatMap((tabs) => {
        if (tabs.length === 0) {
          this.toIndex();
          this.loading = false;
          return of([])
        }
        return from(tabs).pipe(
          concatMap((tab, ti) => this.elementsService.getByTabId(tab.id!).pipe(
            take(1),
            tap(resp => {
              resp.forEach((el: Element) => {
                this.currentLesson.tabs![ti].pages![el.pageNumber!]?.elements!.push(el);
              })
              this.lastSavedLesson.tabs = [...this.currentLesson.tabs ?? []]
              this.toIndex();
              this.loading = false;
              this.lessonLoadedEvent.next('');
            })))
        )
      }
      ),
      toArray(),
      )
  }

  create(body: Lesson): Observable<any> {
    const organizationId = this.userService.getUserData()?.organizationId;
    body.description = "Lección "+body.title+" creada en Levelab. Aprende en un solo click."
    body.organizationId = organizationId;
    return this.http
      .post<any>(this.lessonsBaseUrl, body).pipe(
        map(lesson => { return { ...lesson, type: 'lesson' } }),
      );
  }

  update(id: string, body: Lesson): Observable<any> {
    return this.http.put<any>(this.lessonsBaseUrl + '/' + id, body).pipe(
      tap(() => {
        this.lastSavedLesson = {...this.currentLesson}
        this.lastSavedLesson.tabs = [...this.currentLesson.tabs ?? []];
        this.lastSavedLesson.programs = [...this.currentLesson.programs ?? []]
        this.lastSavedLesson.files = [...this.currentLesson.files ?? []]
      })
    )
  }

  updateCurrent(): Observable<any> {
    const { tabs, ...currentLessonWithoutTabs } = this.currentLesson;
    return this.update(this.currentLesson.id!, currentLessonWithoutTabs);
  }

  updateLessonParams(body: any): Observable<any> {
    const organizationId = this.userService.getUserData()?.organizationId;
    body.organizationId = organizationId;
    return this.http.put<any>(`${this.lessonsBaseUrl}/updateLessonParams`, body).pipe(
      tap(() => {
      })
    )
  }

  duplicate(id: string, courseId?, newTeacherId?: string): Observable<any> {
    let body: any = {};
    const organizationId = this.userService.getUserData()?.organizationId;
  
    if (courseId) {
      body.courseId = courseId;
    }
  
    if (newTeacherId) {
      body.newTeacherId = newTeacherId;
    }

    body.organizationId = organizationId;

    return this.http.post<any>(`${this.lessonsBaseUrl}Duplicate/${id}`, body)
  }

  delete(id: string): Observable<any> {
    return this.http.delete<any>(this.lessonsBaseUrl + '/' + id)
  }

  addCollaborator(userId: string, lessonId: string, group: any): Observable<any> {
    // console.log(userId, lessonId, group)
    return this.http
      .post<any>(`${this.lessonsBaseUrl}/addCollaborator`, {userId, lessonId, group});
  }

  deleteCollaborator(userId: string, lessonId: string, group: any): Observable<any> {
    // console.log(userId, lessonId, group)
    return this.http
      .post<any>(`${this.lessonsBaseUrl}/deleteCollaborator`, {userId, lessonId, group});
  }

  setLessonImage(lessonId: string, file: string, extension: string, contentType: string): Observable<any> {
    return this.http
      .post<any>(`${this.imagesBaseUrl}/${lessonId}`, {
        extension,
        file,
        contentType,
        table: "Lessons"
      })
  }

  uploadLessonAttachmentFile(lessonId: string, file: string, extension: string, contentType: string, filename: string): Observable<any> {
    const organizationId = this.userService.getUserData()?.organizationId;
    return this.http
      .post<any>(`${this.attachmentsBaseUrl}/${lessonId}`,
        {
          file,
          extension,
          contentType,
          filename,
          organizationId
        })
  }

  getPresignedUrl(lessonId: string, extension: string, contentType: string, filename: string): Observable<any> {
    const organizationId = this.userService.getUserData()?.organizationId;
    return this.http
      .post<any>(`${this.attachmentsBaseUrl}/getPresignedUrl/${lessonId}`,
        {
          extension,
          contentType,
          filename,
          organizationId
        })
  }

  uploadFileToS3(presignedUrl: string, file): Observable<any> {
    return this.http.put(presignedUrl, file.source, { headers: { 'Content-Type': file.contentType,
                                                                 'X-Skip-Interceptor': 'true' },
                                                      observe: 'response' });
  }

  deleteLessonAttachmentFile(s3route: string): Observable<any> {
    const organizationId = this.userService.getUserData()?.organizationId;
    return this.http
      .delete<any>(`${this.attachmentsBaseUrl}/lesson`,
      {body: {fileName: s3route, organizationId}})
  }

  toIndex(): void {
    this.lessonIndex = {
      title: this.currentLesson.title!,
      tabs: this.currentLesson.tabs!.map(tab => {
        return { title: tab.title!, pages: [...Array(tab.pages!.length).keys()], editing: false }
      })
    }
  }

  getPageQuizzes(tabIndex: number, pageIndex: number): any[] {
    let lessonQuizzes: any[] = [];
    let quizzes =  this.currentLesson.tabs![tabIndex].pages![pageIndex].elements?.filter(el => {
      return !!el.data.question
    }) ?? [];
    quizzes?.forEach((quiz) => {
      quiz.data.uniqueAnswers = this.currentLesson.uniqueAnswers;
      lessonQuizzes.push(quiz);
    })
    return lessonQuizzes;
  }

  getSolutionsQuizzes(token: string, lessonId: string, sessionId: string) {
    return this.http.post(`https://${environment.baseUrl}/php/getQuizzes.php`, {token, lessonId, sessionId})
  }

  hasContent(): boolean {
    for (let tab of this.currentLesson.tabs ?? []) {
      for (let page of tab.pages ?? []) {
        if (page.elements && page.elements.length > 0) return true;
      }
    }
    return false;
  }

  isModified(): boolean {
    let currentLesson = {...this.currentLesson};
    let currentTabs = [... currentLesson.tabs ?? []];
    let currentPrograms = [...this.currentLesson.programs ?? []];
    delete currentLesson.tabs;
    delete currentLesson.programs;

    let lastSavedLesson = {...this.lastSavedLesson};
    let lastSavedTabs = [... lastSavedLesson.tabs ?? []];
    let lastSavedPrograms = [...this.lastSavedLesson.programs ?? []];
    delete lastSavedLesson.tabs;
    delete lastSavedLesson.programs;

    const isLessonInfoModified = !isEqual(currentLesson, lastSavedLesson);
    const isLessonProgramsModified = !isEqual(currentPrograms, lastSavedPrograms);
    const isLessonTabsModified = this.areTabsModified(currentTabs, lastSavedTabs);

    return isLessonInfoModified || isLessonProgramsModified || isLessonTabsModified || this.elementsDeleted;
  }

  private areTabsModified(currentTabs: LessonTab[], lastSavedTabs: LessonTab[]): boolean {
    for (const currentTab of currentTabs) {
      if (this.isTabModified(currentTab, lastSavedTabs)) {
        return true;
      }

      let pageNumber = 0;
      const matchingTab = lastSavedTabs.find(tab => tab.id === currentTab.id);

      for (const page of currentTab.pages ?? []) {
        if (this.areElementsModified(page.elements ?? [], currentTab, pageNumber, [...matchingTab?.pages![pageNumber].elements ?? []])) {
          return true;
        }
        pageNumber += 1;
      }
    }

    // If the iteration completes without returning true, no differences were found
    return false;
  }


  private isTabModified(currentTab: LessonTab, lastSavedTabs: LessonTab[]): boolean {
    const { id, tabOrder, npages } = currentTab;

    if (id === undefined) {
      return true;
    }

    const matchingTab = lastSavedTabs.find(tab => tab.id === id);

    return !matchingTab || (matchingTab.tabOrder !== tabOrder || matchingTab.npages !== npages);
  }


  private areElementsModified(
    elements: Element[],
    currentTab: LessonTab,
    pageNumber: number,
    lastSavedElements: Element[]
    ): boolean {

    let elementOrder = 0;

    for (const element of elements ?? []) {
      // filter if there's a matching element based on elementId
      lastSavedElements = lastSavedElements.filter(
        savedElement => element.id !== savedElement.id
      );

      element.tabId = currentTab.id;
      element.pageNumber = pageNumber;

      element.isModified!(elementOrder, pageNumber);

      if (element.modified) {
        return true;
      }
      elementOrder += 1;
    }

    // if any element remains on the lastSavedElements list, it means that it was deleted
    return lastSavedElements.length > 0;
  }
}